import _ from 'lodash';
import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import { composePosition } from 'quill/modules/tableEmbed';
import type { TableChange } from '../table/types';
import { parseCellIdentity, stringifyCellIdentity } from '../table/utils';
import transformRange from '../transformRange';

interface PlainMark {
  type: 'plain';
  index: number;
  length: number;
}

interface CellMark {
  type: 'cell';
  index: number;
  cell: {
    identity: string;
    index: number;
    length: number;
  };
}

interface RowMark {
  type: 'row';
  index: number;
  rows: number[];
}

interface ColumnMark {
  type: 'column';
  index: number;
  columns: number[];
}

interface CodeMark {
  type: 'code';
  index: number;
  code: {
    index: number;
    length: number;
  };
}

type Mark = PlainMark | CellMark | RowMark | ColumnMark | CodeMark;

const getOpAt = (delta: Delta, index: number): Op | undefined =>
  new Delta(delta.filter(({ insert }) => !insert)).slice(index).ops[0];

export function transformMark(
  mark: Mark,
  delta: Delta,
  priority = false,
): Mark | null {
  switch (mark.type) {
    case 'plain': {
      return { type: 'plain', ...transformRange(mark, delta, priority) };
    }
    case 'cell':
    case 'row':
    case 'column': {
      const index = delta.transform(mark.index, priority);
      const op = getOpAt(delta, mark.index);

      // No changes made to table
      if (!op) return { ...mark, index };

      // Table is deleted
      if (op.delete) return { type: 'plain', index, length: 0 };

      const table: TableChange =
        (typeof op.retain === 'object' && op.retain?.['table-embed']) || {};

      if (mark.type === 'cell') {
        let row: number | null = null;
        let column: number | null = null;
        [row, column] = parseCellIdentity(mark.cell.identity);
        row = composePosition(new Delta(table.rows), row);
        column = composePosition(new Delta(table.columns), column);

        // Cell is deleted
        if (row === null || column === null) return null;

        const identity = stringifyCellIdentity(row, column);
        return {
          type: 'cell',
          index,
          cell: {
            identity,
            ...transformRange(
              mark.cell,
              new Delta(table.cells?.[identity]?.content),
              priority,
            ),
          },
        };
      }

      const change = new Delta(
        mark.type === 'row' ? table.rows : table.columns,
      );
      const indexes = mark.type === 'row' ? mark.rows : mark.columns;

      return {
        ...mark,
        [mark.type === 'row' ? 'rows' : 'columns']: _.compact(
          indexes.map((i) => composePosition(change, i)),
        ),
      };
    }
    case 'code': {
      const index = delta.transform(mark.index, priority);
      const op = getOpAt(delta, mark.index);

      if (!op) return { ...mark, index };
      if (op.delete) return { type: 'plain', index, length: 0 };

      return {
        ...mark,
        code: transformRange(
          mark.code,
          new Delta(
            // @ts-expect-error
            (typeof op.retain === 'object' && op.retain?.['code-embed']) || [],
          ),
          priority,
        ),
      };
    }
    default:
      return null;
  }
}

export default Mark;
